import { createSelector } from 'reselect'
import { messageMatchesActiveChatContext } from './chatThreadMessages'
import { isDangerMessage } from './isDangerMessage'
import { isIncomingOperatorMessage } from './isIncomingOperatorMessage'
import { getDataStoreEntityMap } from './dataStoreEntityMap'

const selectProfileId = state => state.experiences.auth.profile?.id
const selectMessagesById = state => getDataStoreEntityMap(state.data.messages)
const selectDevicesById = state => getDataStoreEntityMap(state.data.devices)
const selectMessageCategoriesById = state => getDataStoreEntityMap(state.data.messageCategories)
const selectActiveContext = state => state.experiences.chatManagement.activeContext
const selectChatDrawerOpen = state => state.ui.layout.chatDrawerOpen
const selectChatDrawerPanel = state => state.ui.layout.chatDrawerPanel

/**
 * Total unread main-chat notifications and whether any unread is danger-class.
 * Device-thread (1:1) unreads only; group unreads use header group buttons.
 * Memoized so useSelector keeps a stable object reference when inputs are unchanged.
 * @param {object} state - Redux root state
 */
export const selectMainChatUnreadBadge = createSelector(
  [
    selectProfileId,
    selectMessagesById,
    selectDevicesById,
    selectMessageCategoriesById,
    selectActiveContext,
    selectChatDrawerOpen,
    selectChatDrawerPanel
  ],
  (
    profileId,
    messagesById,
    devicesById,
    messageCategoriesById,
    activeContext,
    chatDrawerOpen,
    chatDrawerPanel
  ) => {
    let count = 0
    let hasDanger = false

    if (!profileId) {
      return { count, hasDanger }
    }

    const isMainChatDrawerOpen =
      chatDrawerOpen && chatDrawerPanel === 'main'

    for (const message of Object.values(messagesById)) {
      if (message.read) continue
      if (!isIncomingOperatorMessage(message, profileId)) continue
      if (
        isMainChatDrawerOpen &&
        messageMatchesActiveChatContext(message, activeContext, profileId, devicesById)
      ) {
        continue
      }

      if (isDangerMessage(message, messageCategoriesById)) {
        hasDanger = true
      }

      // Group totals come from the server; only count device (1:1) threads here.
      if (!message.receiverChatGroupId) {
        count += 1
      }
    }

    return { count, hasDanger }
  }
)
